import { dateToApiFormat, dateToDateOnlyApiFormat } from '@/helpers/dateHelper';
import { OptimiserRequest, useSendOptimiseRequest } from '@/services/optimiser/useSendOptimseRequest';
import { useTemplatesForLocation } from '@/services/shared/useTemplatesForLocation';
import { FC, useCallback, useEffect, useState } from 'react';
import { GiAtom } from 'react-icons/gi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageHeader } from '../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { AccordionWizard } from '../../../components/shared/Accordion/AccordionWizard';
import { Button } from '../../../components/shared/button/Button';
import { TSCard } from '../../../components/shared/card/Card';
import strings from '../roster.translations';
import { OptimiserDateRange } from './OptimiserDateRange';
import { buildRequestedDays, getOptimiserDateBounds, OptimiserSelection } from '@/services/optimiser/optimiserDates';
import OptimiserSelectTemplates from './OptimiserSelectTemplates';
import { useOptimiserSolverWizard } from './useOptimiserSolverWizard';

export const OptimserPage: FC = () => {
  const { state } = useLocation();
  const date = state.selectedMonth;

  const returnLink = `/rosters/calendar/${dateToApiFormat(date)}`;

  const breadcrumbs = [
    { name: strings.rosterBreadcrumbTitle, to: returnLink },
    {
      name: strings.optimiseRosters.title,
      to: '/rosters/optimise'
    }
  ];

  const { minStartDate, defaultEndDate } = getOptimiserDateBounds(date, new Date());

  const [startDate, setStartDate] = useState<Date | undefined>(minStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);

  const datesValid = endDate && startDate && endDate > startDate;

  const onChangeDates = useCallback(
    (newStart?: Date, newEnd?: Date) => {
      setStartDate(newStart);
      setEndDate(newEnd);
    },
    [setStartDate, setEndDate]
  );

  const [requestedDays, setRequestedDays] = useState<OptimiserSelection[]>([]);
  const allDaysRequested = requestedDays.every(r => r.templateId !== undefined);
  const { templateOptions } = useTemplatesForLocation();
  const { effectiveSolver, effectiveSolverLabel, solverWizardChoice } = useOptimiserSolverWizard();

  useEffect(() => {
    /*
     * Adjust the requested days array if start date or end date changes.
     */
    if (!datesValid) {
      // If either is not defined, completely wipe the array.
      setRequestedDays([]);
      return;
    }

    if (requestedDays.length > 0) {
      // If there's an existing array, check if the dates have changed.
      const firstDate = requestedDays[0].date;
      const lastDate = requestedDays[requestedDays.length - 1].date;

      const firstDateMatch = dateToDateOnlyApiFormat(firstDate) === dateToDateOnlyApiFormat(startDate);
      const lastDateMatch = dateToDateOnlyApiFormat(lastDate) === dateToDateOnlyApiFormat(endDate);

      if (firstDateMatch && lastDateMatch) {
        // If the dates haven't changed, no action is required.
        return;
      }
    }

    // Build a new requested days array.
    setRequestedDays(buildRequestedDays(startDate, endDate, requestedDays, templateOptions));
  }, [startDate, endDate, requestedDays, templateOptions, datesValid]);

  const navigate = useNavigate();

  const onOptimiseComplete = useCallback(() => {
    navigate(returnLink);
  }, []);

  const { sendOptimization, loading, error } = useSendOptimiseRequest(onOptimiseComplete);

  const wizardChoices = [
    {
      heading: strings.optimiseRosters.selectRosterDates,
      component: (
        <OptimiserDateRange
          startDate={startDate}
          endDate={endDate}
          changeDatesCallback={onChangeDates}
          minStartDate={minStartDate}
        />
      ),
      nextDisabled: !datesValid
    },
    {
      heading: strings.optimiseRosters.selectTemplates,
      component: <OptimiserSelectTemplates existingPattern={requestedDays} setPatternCallback={setRequestedDays} />,
      nextDisabled: !allDaysRequested
    },
    ...(solverWizardChoice ? [solverWizardChoice] : []),
    {
      heading: strings.optimiseRosters.createTabHeader,
      component: (
        <div className="flex flex-col gap-2">
          <p>{startDate && endDate && strings.optimiseRosters.submit.createPeriod(startDate!, endDate!)}</p>
          <p>{strings.optimiseRosters.submit.selectedSolver(effectiveSolverLabel)}</p>
          <p>{strings.optimiseRosters.submit.autofillTime}</p>
          <p>{strings.optimiseRosters.submit.start}</p>
          {error && <p className="text-red-500">{error}</p>}
        </div>
      )
    }
  ];

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.cardTitle} breadcrumbs={breadcrumbs} icon={<GiAtom />} />}
    >
      <PageSizer className="flex flex-col w-full p-5" mode={SizerMode.PAGE}>
        <TSCard className="p-5">
          <AccordionWizard
            choices={wizardChoices}
            customSubmit={
              <Button
                disabled={!allDaysRequested}
                primary
                onClick={() => sendOptimization(requestedDays as OptimiserRequest[], effectiveSolver)}
                loading={loading}
              >
                {strings.optimiseRosters.submitText}
              </Button>
            }
          />
        </TSCard>
      </PageSizer>
    </PageContainer>
  );
};
